import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import PostHogReachabilityContext from './PostHogReachabilityContext'
import {
  checkPostHogReachability,
  type PostHogReachabilityResult,
} from './checkPostHogReachability'
import { reportPostHogHostBlocked } from './reportPostHogHostBlocked'
import { POSTHOG_API_HOST } from '../../constants'

/** Wait for posthog-js `/decide` before sending our own backup probe. */
export const POSTHOG_BACKUP_PROBE_DELAY_MS = 4_000

type CheckFn = (options: { host: string; apiKey: string }) => Promise<PostHogReachabilityResult>

type FeatureFlagsLoadContext = {
  errorsLoading?: boolean
}

type Props = {
  children: React.ReactNode
  apiKey: string | undefined
  check?: CheckFn
  host?: string
}

/**
 * When the NodaFi API is reachable but PostHog is not, feature flags stay
 * false — which is how Storage Pro lost procedures on web (NOD-3773 / Brave
 * shields and corporate firewalls).
 *
 * Primary signal: posthog-js `onFeatureFlags` with `errorsLoading: true`
 * (the `/decide` request failed). Backup: our own probe of that same endpoint,
 * delayed so we don't race the SDK's `/decide` on every page load.
 */
const PostHogReachabilityProvider = ({
  children,
  apiKey,
  check = checkPostHogReachability,
  host = POSTHOG_API_HOST,
}: Props): React.ReactElement => {
  const posthog = usePostHog()
  const [isBlocked, setIsBlocked] = useState(false)
  const probeIdRef = useRef(0)
  const flagsLoadedFromRemoteRef = useRef(false)
  const backupProbeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const checkRef = useRef(check)
  checkRef.current = check

  const invalidateInFlightProbe = useCallback(() => {
    probeIdRef.current += 1
  }, [])

  const markBlocked = useCallback(
    (reason: 'network_error' | 'timeout' | 'html_block_page') => {
      if (flagsLoadedFromRemoteRef.current) return
      setIsBlocked(true)
      reportPostHogHostBlocked({ host, reason })
    },
    [host],
  )

  const markFlagsLoaded = useCallback(() => {
    flagsLoadedFromRemoteRef.current = true
    invalidateInFlightProbe()
    setIsBlocked(false)
  }, [invalidateInFlightProbe])

  const runProbe = useCallback(() => {
    if (apiKey == null || apiKey === '') {
      setIsBlocked(false)
      return
    }
    if (navigator.onLine === false) {
      setIsBlocked(false)
      return
    }
    if (flagsLoadedFromRemoteRef.current) return

    probeIdRef.current += 1
    const probeId = probeIdRef.current

    void checkRef
      .current({ host, apiKey })
      .then((result) => {
        if (probeId !== probeIdRef.current) return

        if (result.status === 'blocked') {
          markBlocked(result.reason)
        }
        // Do not clear on probe success — Brave often blocks `/decide` for the SDK
        // while a separate fetch can still get through. `onFeatureFlags` with
        // `errorsLoading: false` is the signal that flags actually loaded.
      })
      .catch(() => {
        if (probeId !== probeIdRef.current) return
        markBlocked('network_error')
      })
  }, [apiKey, host, markBlocked])

  const runProbeIfGraceElapsed = useCallback(() => {
    if (backupProbeTimeoutRef.current != null) return
    runProbe()
  }, [runProbe])

  useEffect(() => {
    if (posthog == null || typeof posthog.onFeatureFlags !== 'function') return undefined

    return posthog.onFeatureFlags(
      (
        _flags: string[],
        _variants: Record<string, string | boolean>,
        context?: FeatureFlagsLoadContext,
      ) => {
        if (context?.errorsLoading === true) {
          flagsLoadedFromRemoteRef.current = false
          markBlocked('network_error')
          return
        }
        if (context?.errorsLoading === false) {
          markFlagsLoaded()
        }
      },
    )
  }, [posthog, markBlocked, markFlagsLoaded])

  useEffect(() => {
    backupProbeTimeoutRef.current = setTimeout(() => {
      backupProbeTimeoutRef.current = null
      runProbe()
    }, POSTHOG_BACKUP_PROBE_DELAY_MS)

    return () => {
      if (backupProbeTimeoutRef.current != null) {
        clearTimeout(backupProbeTimeoutRef.current)
        backupProbeTimeoutRef.current = null
      }
      invalidateInFlightProbe()
    }
  }, [runProbe, invalidateInFlightProbe])

  useEffect(() => {
    const handleOnline = () => runProbeIfGraceElapsed()
    const handleOffline = () => {
      invalidateInFlightProbe()
      setIsBlocked(false)
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') runProbeIfGraceElapsed()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [runProbeIfGraceElapsed, invalidateInFlightProbe])

  const value = useMemo(() => ({ isBlocked }), [isBlocked])

  return (
    <PostHogReachabilityContext.Provider value={value}>
      {children}
    </PostHogReachabilityContext.Provider>
  )
}

export default PostHogReachabilityProvider
